/** Keys match quill-table-better language packs. Border style values stay CSS keywords. */
export const QUILL_TABLE_TEXT_KEYS = [
    "col",
    "insColL",
    "insColR",
    "delCol",
    "selCol",
    "row",
    "headerRow",
    "insRowAbv",
    "insRowBlw",
    "delRow",
    "selRow",
    "mCells",
    "sCell",
    "tblProps",
    "cellProps",
    "insParaOTbl",
    "insB4",
    "insAft",
    "copyTable",
    "delTable",
    "border",
    "color",
    "width",
    "background",
    "dims",
    "height",
    "padding",
    "tblCellTxtAlm",
    "alCellTxtL",
    "alCellTxtC",
    "alCellTxtR",
    "jusfCellTxt",
    "alCellTxtT",
    "alCellTxtM",
    "alCellTxtB",
    "dimsAlm",
    "alTblL",
    "tblC",
    "alTblR",
    "save",
    "cancel",
    "colorMsg",
    "dimsMsg",
    "colorPicker",
    "removeColor",
    "black",
    "dimGrey",
    "grey",
    "lightGrey",
    "white",
    "red",
    "orange",
    "yellow",
    "lightGreen",
    "green",
    "aquamarine",
    "turquoise",
    "lightBlue",
    "blue",
    "purple",
] as const;

export type QuillTableTextKey = (typeof QUILL_TABLE_TEXT_KEYS)[number];

export type QuillTableTexts = Record<QuillTableTextKey, string>;

const MENU_TOOLTIP_KEY: Record<string, QuillTableTextKey> = {
    column: "col",
    row: "row",
    merge: "mCells",
    table: "tblProps",
    cell: "cellProps",
    wrap: "insParaOTbl",
    delete: "delTable",
    copy: "copyTable",
};

/** Child order matches quill-table-better menu config insertion order. */
const MENU_ITEM_KEYS: Record<string, QuillTableTextKey[]> = {
    column: ["insColL", "insColR", "delCol", "selCol"],
    row: ["headerRow", "insRowAbv", "insRowBlw", "delRow", "selRow"],
    merge: ["mCells", "sCell"],
    wrap: ["insB4", "insAft"],
};

interface QuillTableLanguageApi {
    registry: (name: string, content: QuillTableTexts) => void;
    changeLanguage: (name: string) => void;
    useLanguage: (key: string) => string;
}

const COLOR_NAME_KEYS: QuillTableTextKey[] = [
    "black",
    "dimGrey",
    "grey",
    "lightGrey",
    "white",
    "red",
    "orange",
    "yellow",
    "lightGreen",
    "green",
    "aquamarine",
    "turquoise",
    "lightBlue",
    "blue",
    "purple",
];

const FORM_OBSERVERS = new WeakMap<HTMLElement, { texts: QuillTableTexts }>();
const ORIGINAL_USE_LANGUAGE = new WeakMap<QuillTableLanguageApi, (key: string) => string>();

export const buildQuillTableTexts = (
    translate: (key: QuillTableTextKey) => string,
): QuillTableTexts => {
    const texts = {} as QuillTableTexts;
    for (const key of QUILL_TABLE_TEXT_KEYS) {
        texts[key] = translate(key);
    }
    return texts;
};

const applyMenuTexts = (container: HTMLElement, texts: QuillTableTexts) => {
    const menus = container.querySelector(".ql-table-menus-container");
    if (!menus) return;

    for (const [category, tooltipKey] of Object.entries(MENU_TOOLTIP_KEY)) {
        const menu = menus.querySelector(`[data-category="${category}"]`);
        if (!menu) continue;

        const tooltip = menu.querySelector(".ql-table-tooltip");
        if (tooltip) tooltip.textContent = texts[tooltipKey];

        const itemKeys = MENU_ITEM_KEYS[category];
        if (!itemKeys) continue;

        const items = menu.querySelectorAll(
            ":scope > .ql-table-dropdown-list > li:not(.ql-table-divider)",
        );
        itemKeys.forEach((itemKey, index) => {
            const item = items[index];
            if (!item) return;
            if (item.classList.contains("ql-table-header-row")) {
                const label = item.querySelector(":scope > span:not(.ql-table-switch)");
                if (label) label.textContent = texts[itemKey];
                return;
            }
            item.textContent = texts[itemKey];
        });
    }
};

const setFieldLabel = (field: Element | null, label: string, message?: string) => {
    if (!field) return;
    const labelEl = field.querySelector(":scope > .label-field-view-input-wrapper > label");
    if (labelEl) labelEl.textContent = label;
    const input = field.querySelector(":scope > .label-field-view-input-wrapper > input");
    if (input) input.setAttribute("placeholder", label);
    if (!message) return;
    const status = field.querySelector(":scope > .label-field-view-status");
    if (status) status.textContent = message;
};

const setTooltip = (host: Element | undefined, text: string) => {
    const tip = host?.querySelector(":scope > .ql-table-tooltip");
    if (tip) tip.textContent = text;
};

const applyColorPickerTexts = (container: Element | null, texts: QuillTableTexts) => {
    if (!container) return;
    setFieldLabel(
        container.querySelector(":scope > .label-field-view"),
        texts.color,
        texts.colorMsg,
    );

    const picker = container.querySelector(":scope > .color-picker > .color-picker-select");
    if (!picker) return;

    const eraseButtons = picker.querySelectorAll(".erase-container > button");
    if (eraseButtons[0]) eraseButtons[0].textContent = texts.removeColor;
    if (eraseButtons[1]) eraseButtons[1].textContent = texts.colorPicker;

    const swatches = picker.querySelectorAll(":scope > .color-list > li");
    COLOR_NAME_KEYS.forEach((key, index) => {
        setTooltip(swatches[index], texts[key]);
    });
};

const applyCheckTooltips = (
    container: Element | null,
    keys: QuillTableTextKey[],
    texts: QuillTableTexts,
) => {
    if (!container) return;
    const buttons = container.querySelectorAll(":scope > .ql-table-tooltip-hover");
    keys.forEach((key, index) => {
        setTooltip(buttons[index], texts[key]);
    });
};

const applySectionLabel = (row: Element | undefined, text: string) => {
    const label = row?.querySelector(":scope > .ql-table-dropdown-label");
    if (label) label.textContent = text;
};

/** Border style options stay CSS keywords (`solid`, `dashed`, …) because they are saved as-is. */
const applyPropertiesFormTexts = (form: HTMLElement, texts: QuillTableTexts) => {
    const rows = Array.from(form.querySelectorAll(":scope > .properties-form-row"));
    const isCell = form.querySelectorAll(".ql-table-check-container").length >= 2;
    const header = form.querySelector(":scope > .properties-form-header");
    if (header) header.textContent = texts[isCell ? "cellProps" : "tblProps"];

    applySectionLabel(rows[0], texts.border);
    applySectionLabel(rows[1], texts.background);
    applyColorPickerTexts(rows[0]?.querySelector(":scope > .ql-table-color-container") ?? null, texts);
    setFieldLabel(rows[0]?.querySelector(":scope > .label-field-view") ?? null, texts.width, texts.dimsMsg);
    applyColorPickerTexts(rows[1]?.querySelector(":scope > .ql-table-color-container") ?? null, texts);

    if (isCell) {
        applySectionLabel(rows[2], texts.dims);
        applySectionLabel(rows[3], texts.tblCellTxtAlm);
        const dims = rows[2]?.querySelectorAll(":scope > .label-field-view") ?? [];
        setFieldLabel(dims[0] ?? null, texts.width, texts.dimsMsg);
        setFieldLabel(dims[1] ?? null, texts.height, texts.dimsMsg);
        setFieldLabel(dims[2] ?? null, texts.padding, texts.dimsMsg);
        const alignGroups = rows[3]?.querySelectorAll(":scope > .ql-table-check-container") ?? [];
        applyCheckTooltips(
            alignGroups[0] ?? null,
            ["alCellTxtL", "alCellTxtC", "alCellTxtR", "jusfCellTxt"],
            texts,
        );
        applyCheckTooltips(
            alignGroups[1] ?? null,
            ["alCellTxtT", "alCellTxtM", "alCellTxtB"],
            texts,
        );
    } else {
        applySectionLabel(rows[2], texts.dimsAlm);
        const dims = rows[2]?.querySelectorAll(":scope > .label-field-view") ?? [];
        setFieldLabel(dims[0] ?? null, texts.width, texts.dimsMsg);
        setFieldLabel(dims[1] ?? null, texts.height, texts.dimsMsg);
        applyCheckTooltips(
            rows[2]?.querySelector(":scope > .ql-table-check-container") ?? null,
            ["alTblL", "tblC", "alTblR"],
            texts,
        );
    }

    form.querySelectorAll(":scope > .properties-form-action-row > button").forEach((button) => {
        const key = button.getAttribute("label");
        if (key !== "save" && key !== "cancel") return;
        const spans = button.querySelectorAll(":scope > span");
        const textSpan = spans[spans.length - 1];
        if (!textSpan || textSpan.querySelector("svg")) return;
        textSpan.textContent = texts[key];
    });
};

const collectForms = (node: Node): HTMLElement[] => {
    if (!(node instanceof HTMLElement)) return [];
    if (node.classList.contains("ql-table-properties-form")) return [node];
    return Array.from(node.querySelectorAll<HTMLElement>(".ql-table-properties-form"));
};

const watchPropertiesForms = (container: HTMLElement, texts: QuillTableTexts) => {
    const existing = FORM_OBSERVERS.get(container);
    if (existing) {
        existing.texts = texts;
        return;
    }

    const state = { texts };
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                collectForms(node).forEach((form) => {
                    applyPropertiesFormTexts(form, state.texts);
                });
            });
        });
    });
    observer.observe(container, { childList: true, subtree: true });
    FORM_OBSERVERS.set(container, state);
};

const installLanguagePack = (language: QuillTableLanguageApi, name: string, texts: QuillTableTexts) => {
    language.registry(name, texts);
    language.changeLanguage(name);
    if (!ORIGINAL_USE_LANGUAGE.has(language)) {
        ORIGINAL_USE_LANGUAGE.set(language, language.useLanguage.bind(language));
    }
    const fallback = ORIGINAL_USE_LANGUAGE.get(language) as (key: string) => string;
    language.useLanguage = (key: string) => texts[key as QuillTableTextKey] || fallback(key);
};

export const applyQuillTableLanguage = (
    editor: { container: HTMLElement; getModule: (name: string) => unknown },
    languageCode: string,
    texts: QuillTableTexts,
) => {
    const tableModule = editor.getModule("table-better") as {
        language?: QuillTableLanguageApi;
        tableMenus?: { tableBetter?: { language?: QuillTableLanguageApi } };
    } | null;
    const languages = [
        tableModule?.language,
        tableModule?.tableMenus?.tableBetter?.language,
    ].filter((language): language is QuillTableLanguageApi => Boolean(language));
    if (!languages.length) return;

    const name = String(languageCode || "ua").split("-")[0] || "ua";
    const unique: QuillTableLanguageApi[] = [];
    languages.forEach((language) => {
        if (unique.indexOf(language) === -1) unique.push(language);
    });
    unique.forEach((language) => installLanguagePack(language, name, texts));
    applyMenuTexts(editor.container, texts);
    editor.container.querySelectorAll<HTMLElement>(".ql-table-properties-form").forEach((form) => {
        applyPropertiesFormTexts(form, texts);
    });
    watchPropertiesForms(editor.container, texts);
};
